import manualLogo from "@/assets/report/manual-logo.png";
import releaseULogo from "@/assets/report/release-u-logo.png";

/**
 * The brands a report can be printed under, picked on the stats page: organizations carry no brand
 * of their own, so whoever exports the report says which one it goes out as. The logos are bundled
 * rather than read from the brand's uploaded artwork so they always make it into the capture — an
 * image from another origin can drop out of an html2canvas render.
 */
export const REPORT_BRANDS = [
    { slug: "RELEASE_U", label: "Release U", logo: releaseULogo },
    { slug: "MANUAL", label: "MANUAL", logo: manualLogo },
] as const;

export type ReportBrand = (typeof REPORT_BRANDS)[number];
export type ReportBrandSlug = ReportBrand["slug"];

export const reportBrand = (slug: ReportBrandSlug): ReportBrand =>
    REPORT_BRANDS.find((brand) => brand.slug === slug) ?? REPORT_BRANDS[0];

export interface ReportEvent {
    name: string;
    date: string;
    attendees: string;
    /** Format for a virtual event, location or chapter for an in-person one. */
    detail: string;
    notes: string;
}

export interface ReportAudience {
    title: string;
    body: string;
    nextStep: string;
}

/**
 * Everything in the report that is written rather than counted. Each part prints only when
 * something has been written in it, so an empty field leaves its section out of the PDF.
 */
export interface ReportNarrative {
    preparedBy: string;
    monthInBrief: string;
    virtualEvent: ReportEvent;
    inPersonEvent: ReportEvent;
    testimonial: { quote: string; attribution: string };
    audiences: ReportAudience[];
}

const emptyEvent = (): ReportEvent => ({ name: "", date: "", attendees: "", detail: "", notes: "" });

/** "What this means for you" cards, as in the template. */
export const AUDIENCE_CARD_COUNT = 3;

/**
 * Nothing written yet. Audience headings start empty too: who a card speaks to depends on the
 * partner, so the exporter names it.
 */
export const emptyNarrative = (preparedBy = ""): ReportNarrative => ({
    preparedBy,
    monthInBrief: "",
    virtualEvent: emptyEvent(),
    inPersonEvent: emptyEvent(),
    testimonial: { quote: "", attribution: "" },
    audiences: Array.from({ length: AUDIENCE_CARD_COUNT }, () => ({ title: "", body: "", nextStep: "" })),
});

export const hasText = (value: string): boolean => value.trim().length > 0;

export const isEventWritten = (event: ReportEvent): boolean => Object.values(event).some(hasText);

/** An audience prints once it says something; a heading alone has nothing to say. */
export const isAudienceWritten = (audience: ReportAudience): boolean =>
    hasText(audience.body) || hasText(audience.nextStep);

export const isTestimonialWritten = (testimonial: ReportNarrative["testimonial"]): boolean =>
    hasText(testimonial.quote);
